import { useEffect } from "react";
import { Box } from "@mantine/core";
import {
  DataTable, EmptyRow, Prompt, Screen, Sparkline, type Column,
  LoadError,
}  from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { bar, compact, percent } from "../lib/vf";
import type { NamespaceTraffic } from "../types/domain";
import css from "./screens.module.css";

const COLUMNS: Column[] = [
  { key: "namespace", label: "namespace", width: "2.2fr" },
  { key: "searches", label: "searches", width: "1fr" },
  { key: "p95", label: "p95", width: "0.8fr" },
  { key: "cache", label: "cache hit", width: "1fr" },
  { key: "errors", label: "errors", width: "0.8fr" },
];

export default function Analytics() {
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const analytics = useConsoleStore((s) => s.analytics);
  const loadError = useConsoleStore((s) => s.errors.analytics);
  const load = useConsoleStore((s) => s.loadAnalytics);

  useEffect(() => {
    if (workspaceId) void load();
  }, [workspaceId, load]);

  const a = analytics;

  return (
    <Screen>
      <Prompt command="vf stats --since 24h" />
      <LoadError message={loadError} />

      <Box className={css.tiles4} mb={16}>
        <Tile label="searches · 24h" value={a ? compact(a.searchesTotal) : "—"} />
        <Tile label="p95" value={a?.p95Ms != null ? `${a.p95Ms} ms` : "—"} />
        <Tile label="p50" value={a?.p50Ms != null ? `${a.p50Ms} ms` : "—"} />
        <Tile
          label="error rate"
          value={a?.errorRate != null ? `${(a.errorRate * 100).toFixed(2)}%` : "—"}
          color="var(--vf-ok)"
        />
      </Box>

      <Box
        p="16px 18px"
        mb={16}
        style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}
      >
        <div className={css.fieldLabel}>// searches / hour</div>
        <Sparkline values={a?.searchesSeries ?? []} />
      </Box>

      <Box className={css.split2} mb={16}>
        <Box
          p="16px 18px"
          style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}
        >
          <div className={css.fieldLabel}>// latency p95 / hour</div>
          <Sparkline small values={a?.p95Series ?? []} />
        </Box>

        <Box
          p="16px 18px"
          style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}
        >
          <Box className={css.fieldLabel} mb={12}>
            // cache hierarchy
          </Box>
          <Box fz="12.5px" style={{ lineHeight: 2.1 }}>
            <CacheRow label="L1 mem" fraction={a?.cache.l1 ?? 0} />
            <CacheRow label="L2 kvr" fraction={a?.cache.l2 ?? 0} />
            <CacheRow label="miss" fraction={a?.cache.miss ?? 0} dim />
          </Box>
        </Box>
      </Box>

      <DataTable<NamespaceTraffic>
        columns={COLUMNS}
        rows={a?.namespaces ?? []}
        rowKey={(n) => n.id}
        empty={
          <EmptyRow
            title="no traffic yet"
            body="Per-namespace search volume, latency and cache hit rates appear here once queries start arriving."
          />
        }
        renderCell={(n, key) => {
          switch (key) {
            case "namespace":
              return <span style={{ color: "var(--vf-text)" }}>{n.label}</span>;
            case "searches":
              return <span style={{ color: "var(--vf-acc)" }}>{compact(n.searches)}</span>;
            case "p95":
              return <span style={{ color: "var(--vf-sec)" }}>{n.p95}ms</span>;
            case "cache":
              return <span style={{ color: "var(--vf-ok)" }}>{percent(n.cacheHit)}</span>;
            case "errors":
              return <span style={{ color: "var(--vf-sec)" }}>{n.errors}</span>;
            default:
              return null;
          }
        }}
      />
    </Screen>
  );
}

function Tile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box className={css.statTile}>
      <div className={css.statKey}>{label}</div>
      <div className={css.statValue} style={color ? { color } : undefined}>
        {value}
      </div>
    </Box>
  );
}

function CacheRow({ label, fraction, dim }: { label: string; fraction: number; dim?: boolean }) {
  return (
    <div className={css.cacheRow}>
      <span className={css.cacheLabel}>{label}</span>
      <span className={dim ? css.cacheBarDim : css.cacheBar}>{bar(fraction, 26)}</span>
      <span className={dim ? undefined : css.cacheValue} style={dim ? { color: "var(--vf-sec)" } : undefined}>
        {fraction ? `${Math.round(fraction * 100)}%` : "—"}
      </span>
    </div>
  );
}
